import React, { useEffect, useMemo, useState } from "react";
import "../../services/axios";
import axios from "axios";
import Select from "react-select";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { BASE_URL } from "../Const/Url";
import HelperExportComponent from "../../utils/HelperExportComponent";
import Loader from "../CustomCommons/Loader";

const ThemeReport = () => {
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);

  // filters
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [source, setSource] = useState(""); // "admin" | "website" | "outlet"
  const [outletOptions, setOutletOptions] = useState([]);
  const [selectedOutlet, setSelectedOutlet] = useState(null);

  const hasActiveFilters = !!(startDate || endDate || source || selectedOutlet);

  const handleResetFilters = () => {
    setStartDate("");
    setEndDate("");
    setSource("");
    setSelectedOutlet(null);
  };

  const sourceOptions = [
    { value: "", label: "See All" },
    { value: "admin", label: "Admin" },
    { value: "website", label: "Website" },
    { value: "outlet", label: "Outlet" },
  ];

  // fetch outlet list
  useEffect(() => {
    const fetchOutlets = async () => {
      try {
        const url = `${BASE_URL}api/v1/sales/admin/outlet/`;
        const { data } = await axios.get(url);
        const result = data?.data?.results || [];
        setOutletOptions(result.map((o) => ({ label: o.name, value: o.id })));
      } catch (_e) {
        // swallow; optional list
      }
    };
    fetchOutlets();
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchReport = async () => {
      setLoading(true);
      setError("");
      try {
        const base = `${BASE_URL}api/v1/inventory/admin/theme-sales-report/`;
        const params = new URLSearchParams();
        if (startDate) params.append("start_date", startDate);
        if (endDate) params.append("end_date", endDate);
        if (source) params.append("source", source);
        if (selectedOutlet && source === "outlet")
          params.append("outlet_id", selectedOutlet);
        const url = `${base}?${params.toString()}`;
        const { data } = await axios.get(url);
        if (!isMounted) return;
        const apiData = data?.data || {};
        setRows(Array.isArray(apiData?.rows) ? apiData.rows : []);
        setSummary(apiData?.summary || null);
        if (isMounted) setInitialLoad(false);
      } catch (e) {
        if (!isMounted) return;
        setError(
          e?.response?.data?.message || "Failed to load theme sales report"
        );
        if (isMounted) setInitialLoad(false);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchReport();
    return () => {
      isMounted = false;
    };
  }, [startDate, endDate, source, selectedOutlet]);

  // Extract all category columns (excluding only Theme Name)
  // Include Total (Qty), Total Price, Total Discount from rows
  const [prevCategoryColumns, setPrevCategoryColumns] = useState([]);
  const categoryColumns = useMemo(() => {
    if (rows.length === 0) return [];
    const excludedKeys = ["Theme Name"];
    const allKeys = new Set();

    // Collect all keys from rows
    rows.forEach((row) => {
      Object.keys(row).forEach((key) => {
        if (!excludedKeys.includes(key)) {
          allKeys.add(key);
        }
      });
    });
    const baseColumns = Array.from(allKeys);

    // Sort: put Total (Qty), Total Price, Total Discount at the end
    const totalColumns = ["Total (Qty)", "Total Price", "Total Discount"];
    const otherColumns = baseColumns
      .filter((col) => !totalColumns.includes(col))
      .sort();
    const totalColsInData = totalColumns.filter((col) =>
      baseColumns.includes(col)
    );

    const result = [...otherColumns, ...totalColsInData];
    return result;
  }, [rows]);

  // Preserve previous category columns when loading
  useEffect(() => {
    if (categoryColumns.length > 0) {
      setPrevCategoryColumns(categoryColumns);
    }
  }, [categoryColumns]);

  // Use preserved columns when loading and rows are empty
  const displayColumns =
    loading && rows.length === 0 ? prevCategoryColumns : categoryColumns;

  if (initialLoad && loading) {
    return <Loader />;
  }

  return (
    <div className="mx-3 my-3">
      <div className="cotagorey-head mb-4">
        <div>
          <h3>Theme Report</h3>
        </div>
        <div className="filter_div me-3">
          <div className="d-flex align-items-center">
            <div className="me-2 mb-3 mb-md-0">
              <p className="">Source :</p>
              <div
                className="sl"
                style={{ width: "180px", marginTop: "-15px" }}
              >
                <Select
                  onChange={(opt) => {
                    setSelectedOutlet(null);
                    setSource(opt?.value || "");
                  }}
                  value={
                    sourceOptions.find(
                      (o) => String(o.value) === String(source)
                    ) || sourceOptions[0]
                  }
                  options={sourceOptions}
                  placeholder="---Select---"
                />
              </div>
            </div>
            {source === "outlet" && (
              <div className="me-2 mb-3 mb-md-0">
                <p className="">Outlets :</p>
                <div
                  className="sl"
                  style={{ width: "180px", marginTop: "-15px" }}
                >
                  <Select
                    onChange={(opt) => setSelectedOutlet(opt?.value || null)}
                    value={
                      outletOptions.find((o) => o.value === selectedOutlet) ||
                      null
                    }
                    options={outletOptions}
                    placeholder="---Select---"
                  />
                </div>
              </div>
            )}
          </div>
          <div className="mt-3">
            <DatePicker
              format="YYYY-MM-DD"
              className="date-input"
              placeholder="yyyy-mm-dd"
              style={{ width: 140, height: 40 }}
              onChange={(_, dateString) =>
                setStartDate(Array.isArray(dateString) ? dateString[0] : dateString || "")
              }
              value={startDate ? dayjs(startDate, "YYYY-MM-DD") : null}
            />
            <span className="mx-2 my-auto">To</span>
            <DatePicker
              format="YYYY-MM-DD"
              className="date-input"
              placeholder="yyyy-mm-dd"
              style={{ width: 140, height: 40 }}
              onChange={(_, dateString) =>
                setEndDate(Array.isArray(dateString) ? dateString[0] : dateString || "")
              }
              value={endDate ? dayjs(endDate, "YYYY-MM-DD") : null}
            />
          </div>
          <div
            className="button-div ms-2 my-auto me-2 text-white"
            style={{ background: "black", cursor: "pointer" }}
          >
            <HelperExportComponent
              url={`${BASE_URL}api/v1/inventory/admin/theme-sales-report/?response_format=excel${
                startDate ? `&start_date=${startDate}` : ""
              }${endDate ? `&end_date=${endDate}` : ""}${
                source ? `&source=${source}` : ""
              }${
                selectedOutlet && source === "outlet"
                  ? `&outlet_id=${selectedOutlet}`
                  : ""
              }`}
              fileName={"theme_report.xlsx"}
              items_data={rows}
            />
          </div>
          {hasActiveFilters && (
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm ms-2 mb-2 me-2"
              onClick={handleResetFilters}
            >
              Reset
            </button>
          )}
        </div>
      </div>

      {!!error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {!error && (
        <div style={{ display: "block", overflowX: "auto" }}>
          <table className="table_my">
            <thead
              className="bg-dark text-white head-row"
              style={{ height: "50px", borderTopLeftRadius: "20px !important" }}
            >
              <tr>
                <th
                  style={{
                    fontSize: "16px",
                    fontWeight: "normal",
                    width: "150px",
                    textAlign: "start",
                    paddingLeft: "10px",
                    position: "sticky",
                    left: 0,
                    zIndex: 10,
                    backgroundColor: "#212529",
                  }}
                >
                  Theme Name
                </th>
                {displayColumns.map((category, idx) => (
                  <th
                    key={idx}
                    style={{
                      fontSize: "16px",
                      fontWeight: "normal",
                      minWidth: "120px",
                      textAlign: "center",
                      padding: "10px 8px",
                    }}
                  >
                    {category}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="tb">
              {loading ? (
                <tr>
                  <td colSpan={displayColumns.length + 1}>
                    <div className="d-flex justify-content-center align-items-center py-5">
                      <Loader />
                    </div>
                  </td>
                </tr>
              ) : rows.length > 0 ? (
                <>
                  {rows.map((row, index) => (
                    <tr className="table-row" key={index}>
                      <td
                        style={{
                          paddingLeft: "10px",
                          position: "sticky",
                          left: 0,
                          zIndex: 5,
                          backgroundColor: "white",
                        }}
                      >
                        <p className="ms-3 my-auto">{row["Theme Name"]}</p>
                      </td>
                      {displayColumns.map((category, idx) => (
                        <td
                          key={idx}
                          style={{
                            textAlign: "center",
                            padding: "10px 8px",
                          }}
                        >
                          {row[category] !== undefined ? row[category] : 0}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {/* Summary Rows */}
                  {summary && (
                    <>
                      <tr className="table-row">
                        <td
                          style={{
                            paddingLeft: "10px",
                            position: "sticky",
                            left: 0,
                            zIndex: 5,
                            backgroundColor: "#f8f9fa",
                            fontWeight: "bold",
                          }}
                        >
                          <p className="ms-3 my-auto">Total</p>
                        </td>
                        {displayColumns.map((category, idx) => (
                          <td
                            key={idx}
                            style={{
                              textAlign: "center",
                              padding: "10px 8px",
                              backgroundColor: "#f8f9fa",
                              fontWeight: "bold",
                            }}
                          >
                            {summary.total_quantity?.[category] !== undefined
                              ? summary.total_quantity[category]
                              : 0}
                          </td>
                        ))}
                      </tr>
                      <tr className="table-row">
                        <td
                          style={{
                            paddingLeft: "10px",
                            position: "sticky",
                            left: 0,
                            zIndex: 5,
                            backgroundColor: "#f8f9fa",
                            fontWeight: "bold",
                          }}
                        >
                          <p className="ms-3 my-auto">Total price</p>
                        </td>
                        {displayColumns.map((category, idx) => (
                          <td
                            key={idx}
                            style={{
                              textAlign: "center",
                              padding: "10px 8px",
                              backgroundColor: "#f8f9fa",
                              fontWeight: "bold",
                            }}
                          >
                            {summary.total_price?.[category] !== undefined
                              ? summary.total_price[category]
                              : 0}
                          </td>
                        ))}
                      </tr>
                      <tr className="table-row">
                        <td
                          style={{
                            paddingLeft: "10px",
                            position: "sticky",
                            left: 0,
                            zIndex: 5,
                            backgroundColor: "#f8f9fa",
                            fontWeight: "bold",
                          }}
                        >
                          <p className="ms-3 my-auto">Total Discount</p>
                        </td>
                        {displayColumns.map((category, idx) => (
                          <td
                            key={idx}
                            style={{
                              textAlign: "center",
                              padding: "10px 8px",
                              backgroundColor: "#f8f9fa",
                              fontWeight: "bold",
                            }}
                          >
                            {summary.total_discount?.[category] !== undefined
                              ? summary.total_discount[category]
                              : 0}
                          </td>
                        ))}
                      </tr>
                    </>
                  )}
                </>
              ) : (
                <tr>
                  <td colSpan={displayColumns.length + 1}>
                    <div className="not_found d-flex justify-content-center align-items-center py-4">
                      <h4 className="my-4">No Data Found</h4>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ThemeReport;
